import { MenuButton, forwardRef } from '@chakra-ui/react';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

import type { SuggestionSourceMetadata } from '@frontend/api';
import {
  Box,
  Input,
  InputGroup,
  Menu,
  MenuList,
  Portal,
  Textarea,
  useDisclosure,
} from '@frontend/components';
import { ChangedBy } from '@frontend/services';

import {
  AssistanceAvailableBackground,
  InputGroupWithSuggestionsRightElement,
  InputGroupWithSuggestionsProvider,
  SuggestionMenuItem,
} from './components';
import {
  useAcceptedSuggestionIndex,
  useAutoResizeTextarea,
  useOnClickOutside,
  useOnKeyDown,
} from './hooks';

export type InputGroupWithSuggestionsInputType = 'input' | 'textarea';

export type FieldSuggestionProvenance = {
  filename: string;
  metadata: SuggestionSourceMetadata;
};

export type FieldSuggestionOption = {
  value: string;
  provenance: FieldSuggestionProvenance[];
};

export type InputGroupWithSuggestionsProps = {
  inputType?: InputGroupWithSuggestionsInputType;
  suggestions?: FieldSuggestionOption[];
  initialValue?: string | null;
  onSave?: (v: string | null, changedBy?: ChangedBy) => void;
  isDisabled?: boolean;
  dataAttributes?: Record<string, boolean>;
  id?: string | number;
  annotationsControlCallback?: (
    isOpen: boolean,
    hasBeenOpened: boolean,
  ) => void | null;
  // When provided, the input suggestions menu will mirror this external open state
  externalIsOpen?: boolean;
  externalOnOpen?: () => void;
  externalOnClose?: () => void;
};

const decodeChangedByForInput = ({ key, shiftKey }: ChangedBy) =>
  //If not `Tab`, by process of elimination, the blur event must have been because user clicked outside
  //Note: `Escape` and `Enter` have noop for input field. So if there was a blur it had to be `Tab`
  key === 'Tab' ? { key, shiftKey } : { clickedOutside: true };

const decodeChangedByForTextArea = ({ key, shiftKey }: ChangedBy) =>
  //Currently same implementation as decodeChangedByForInput()
  key === 'Tab' ? { key, shiftKey } : { clickedOutside: true };

export const InputGroupWithSuggestions = forwardRef<
  InputGroupWithSuggestionsProps,
  'div'
>(
  (
    {
      inputType = 'input',
      suggestions = [],
      initialValue,
      onSave,
      isDisabled = false,
      dataAttributes = {},
      id,
      externalIsOpen,
      externalOnOpen,
      externalOnClose,
    },
    formControlRef,
  ) => {
    // State management for popover open/closed.
    const {
      isOpen: internalIsOpen,
      onClose,
      onOpen,
    } = useDisclosure({
      defaultIsOpen: false,
    });

    const isOpen = externalIsOpen ?? internalIsOpen;

    const handleOpen = useCallback(() => {
      onOpen();
      externalOnOpen?.();
    }, [onOpen, externalOnOpen]);
    const internalHandleClose = useCallback(() => {
      onClose();
      externalOnClose?.();
    }, [onClose, externalOnClose]);

    const [lastKey, setLastKey] = useState<ChangedBy>({});
    const suggestionsMenuRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [menuFocus, setMenuFocus] = useState<number>(0);
    const [localValue, setLocalValue] = useState<string | null>(
      initialValue ?? null,
    );
    const [defaultOptionContent, setDefaultOptionsContent] = useState<{
      label: string;
      value: string;
    }>({
      label: `Use "${localValue}"`,
      value: localValue?.trim() ?? '',
    });

    // Update localValue if the initialValue (passed from redux state) changes
    // Optionally listens to changes in 'id' so the initialValue can be reset
    // Can't use 'key' otherwise we lose the context of the passedRef
    useEffect(() => {
      if (initialValue !== undefined) {
        setLocalValue(initialValue);
      }
    }, [initialValue, id]);

    const calculatedRows = useMemo(() => {
      if (!localValue) return 1; // default rows
      const lineCount = localValue.split('\n').length;
      return lineCount;
    }, [localValue]);

    // acceptedSuggestionInd is used to determine highlighted suggestion (menuFocus)
    // acceptedSuggestionIndCaseSensitive is used to determine acceptedSuggestion value + accept/reject state
    const [acceptedSuggestionInd, acceptedSuggestionIndCaseSensitive] =
      useAcceptedSuggestionIndex(localValue, suggestions);

    // Main useEffect
    //    Do nothing until menu isOpen (allows update following onOpen)
    //    Update defaultOptionsContent based on if a value is set or suggestion is accepted (case sensitive)
    //    Update menuFocus (highlighted suggestion) if localValue matches a suggestion (case insensitive)
    useEffect(() => {
      if (!isOpen) {
        return;
      }
      setDefaultOptionsContent(
        localValue
          ? acceptedSuggestionIndCaseSensitive >= 0
            ? { label: 'Clear this field', value: '' }
            : { label: `Use "${localValue}"`, value: localValue.trim() }
          : { label: 'Leave this blank', value: '' },
      );
      if (localValue == null) {
        setMenuFocus(0);
      } else if (acceptedSuggestionInd >= 0) {
        setMenuFocus(acceptedSuggestionInd);
      } else {
        setMenuFocus(suggestions.length);
      }
    }, [
      localValue,
      acceptedSuggestionInd,
      acceptedSuggestionIndCaseSensitive,
      suggestions,
      isOpen,
      initialValue,
    ]);

    const handleClose = useCallback(
      (submitValue?: string | null, changedBy?: ChangedBy) => {
        internalHandleClose();
        onSave?.(
          submitValue !== undefined
            ? submitValue
            : localValue
            ? localValue.trim()
            : localValue,
          ...(changedBy ? [changedBy] : []),
        );
      },
      [localValue, onSave, internalHandleClose],
    );

    const onChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setLocalValue(e.target?.value ?? '');
    };

    // Custom key binds for suggestion menu
    const onKeyDown = useOnKeyDown({
      isOpen,
      menuFocus,
      suggestions,
      defaultOptionContentValue: defaultOptionContent.value,
      handleClose,
      handleOpen,
      setMenuFocus,
      setLocalValue,
    });

    // Auto Resizes text area
    useAutoResizeTextarea(textareaRef);

    // Handle click outside to close menu
    useOnClickOutside({
      isOpen,
      localValue,
      suggestionsMenuRef,
      formControlRef: formControlRef as React.MutableRefObject<HTMLDivElement>,
      handleClose,
    });

    // scroll into view on open if not already
    useEffect(() => {
      if (externalIsOpen !== undefined && isOpen) {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    }, [isOpen, externalIsOpen]);

    if (!suggestions.length) {
      return (
        <InputGroup>
          {inputType === 'input' ? (
            <Input
              ref={inputRef}
              {...dataAttributes}
              onChange={(e) => setLocalValue(e.target?.value ?? '')}
              //Why onKeyDown? in onBlur, we want to know what user action trigger blur (can't use onKeyUp which is real trigger because onBlur fires before onKeyUp)
              onKeyDown={({ key, shiftKey }) =>
                setLastKey({ key, ...(shiftKey && { shiftKey }) })
              }
              onBlur={() =>
                onSave?.(
                  localValue?.trim() || null,
                  decodeChangedByForInput(lastKey),
                )
              }
              value={localValue ?? ''}
              pr={4}
              color="inherit"
              data-fs-hide="true"
              autoComplete="none"
            />
          ) : (
            <Textarea
              ref={textareaRef}
              {...dataAttributes}
              onChange={(e) => setLocalValue(e.target?.value ?? '')}
              onKeyDown={({ key, shiftKey }) =>
                setLastKey({ key, ...(shiftKey && { shiftKey }) })
              }
              onBlur={() =>
                onSave?.(
                  localValue?.trim() || null,
                  decodeChangedByForTextArea(lastKey),
                )
              }
              value={localValue ?? ''}
              h="auto"
              resize="none"
              overflow="hidden"
              minH={4}
              rows={calculatedRows}
              pr={4}
              color="inherit"
              data-fs-hide="true"
            />
          )}
        </InputGroup>
      );
    }

    const acceptedSuggestion = acceptedSuggestionIndCaseSensitive >= 0;
    const availableSuggestions = localValue === null;

    return (
      <InputGroupWithSuggestionsProvider
        value={{
          acceptedSuggestion,
          availableSuggestions,
          isDisabled,
        }}
      >
        <Menu isOpen={isOpen} placement="bottom-end">
          <>
            <InputGroup
              color={acceptedSuggestion ? 'interaction.suggestion' : undefined}
              borderColor={
                availableSuggestions && !isDisabled
                  ? 'interaction.suggestion'
                  : 'separator.border'
              }
              overflow="hidden"
            >
              <>
                {inputType === 'input' ? (
                  <Input
                    ref={inputRef}
                    onFocus={handleOpen}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    value={localValue ?? ''}
                    pr={4}
                    color="inherit"
                    borderColor="inherit"
                    data-fs-hide="true"
                    autoComplete="none"
                  />
                ) : (
                  <Textarea
                    ref={textareaRef}
                    onFocus={handleOpen}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    value={localValue ?? ''}
                    h="auto"
                    resize="none"
                    overflow="hidden"
                    minH={4}
                    rows={calculatedRows}
                    pr={4}
                    color="inherit"
                    borderColor="inherit"
                    data-fs-hide="true"
                  />
                )}
                {availableSuggestions && !isDisabled && (
                  <AssistanceAvailableBackground />
                )}
                <InputGroupWithSuggestionsRightElement />
              </>
            </InputGroup>
            <MenuButton as={Box} />
            <Portal>
              <MenuList
                minW={
                  (formControlRef as React.MutableRefObject<HTMLDivElement>)
                    ?.current?.offsetWidth ?? '300px'
                }
                maxW={`max(${
                  (formControlRef as React.MutableRefObject<HTMLDivElement>)
                    ?.current?.offsetWidth || '0'
                }px, 300px)`}
                maxH="300px"
                overflowY="auto"
                ref={suggestionsMenuRef}
                onMouseLeave={() => {
                  isOpen && setMenuFocus(-1);
                }}
              >
                {suggestions.map((suggestion, ind) => (
                  <SuggestionMenuItem
                    key={suggestion.value}
                    value={suggestion.value}
                    provenance={suggestion.provenance}
                    bg={
                      menuFocus === ind
                        ? 'interaction.surfaceSuggestionHover'
                        : undefined
                    }
                    onClick={() => {
                      setLocalValue(suggestion.value);
                      handleClose(suggestion.value, {
                        suggestionClicked: true,
                      });
                    }}
                    onMouseEnter={() => {
                      setMenuFocus(ind);
                    }}
                    isFocusable={false}
                  />
                ))}
                <SuggestionMenuItem
                  value={defaultOptionContent.label}
                  isRejected={
                    defaultOptionContent.value && acceptedSuggestion
                      ? false
                      : true
                  }
                  bg={
                    menuFocus === suggestions.length
                      ? 'interaction.surfaceSuggestionHover'
                      : undefined
                  }
                  onClick={() => {
                    setLocalValue(defaultOptionContent.value);
                    handleClose(defaultOptionContent.value, {
                      defaultSuggestionClicked: true,
                    });
                  }}
                  onMouseEnter={() => {
                    setMenuFocus(suggestions.length);
                  }}
                />
              </MenuList>
            </Portal>
          </>
        </Menu>
      </InputGroupWithSuggestionsProvider>
    );
  },
);
